import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string; // absolute or root-relative path
  canonicalPath?: string; // e.g. "/employer/new-position"
  type?: 'website' | 'article' | 'product';
  noindex?: boolean;
  faviconHref?: string; // optional override
  ldJson?: Record<string, unknown> | null; // structured data
}

export const useSEO = ({
  title,
  description,
  keywords,
  image,
  canonicalPath,
  type = 'website',
  noindex,
  faviconHref,
  ldJson,
}: SEOProps) => {
  useEffect(() => {
    const head = document.head;
    const absoluteUrl = (() => {
      try {
        const origin = window.location.origin;
        if (canonicalPath) return new URL(canonicalPath, origin).toString();
        return window.location.href;
      } catch {
        return undefined;
      }
    })();

    const ensureMeta = (selector: string, attr: 'name' | 'property', name: string) => {
      let el = head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        head.appendChild(el);
      }
      return el;
    };

    const ensureLink = (rel: string) => {
      let el = head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        head.appendChild(el);
      }
      return el;
    };

    // Title
    document.title = title;

    // Basic metas
    ensureMeta('meta[name="description"]', 'name', 'description').setAttribute('content', description);
    if (keywords) {
      ensureMeta('meta[name="keywords"]', 'name', 'keywords').setAttribute('content', keywords);
    }

    // Canonical
    if (absoluteUrl) {
      ensureLink('canonical').setAttribute('href', absoluteUrl);
    }

    // Open Graph
    ensureMeta('meta[property="og:title"]', 'property', 'og:title').setAttribute('content', title);
    ensureMeta('meta[property="og:description"]', 'property', 'og:description').setAttribute('content', description);
    ensureMeta('meta[property="og:type"]', 'property', 'og:type').setAttribute('content', type);
    if (absoluteUrl) ensureMeta('meta[property="og:url"]', 'property', 'og:url').setAttribute('content', absoluteUrl);
    if (image) ensureMeta('meta[property="og:image"]', 'property', 'og:image').setAttribute('content', image);

    // Twitter
    ensureMeta('meta[name="twitter:card"]', 'name', 'twitter:card').setAttribute('content', image ? 'summary_large_image' : 'summary');
    ensureMeta('meta[name="twitter:title"]', 'name', 'twitter:title').setAttribute('content', title);
    ensureMeta('meta[name="twitter:description"]', 'name', 'twitter:description').setAttribute('content', description);
    if (image) ensureMeta('meta[name="twitter:image"]', 'name', 'twitter:image').setAttribute('content', image);

    // Robots
    if (noindex) {
      ensureMeta('meta[name="robots"]', 'name', 'robots').setAttribute('content', 'noindex, nofollow');
    } else {
      const robots = head.querySelector('meta[name="robots"]');
      if (robots) robots.setAttribute('content', 'index, follow');
    }

    // Favicon override (optional)
    if (faviconHref) {
      ensureLink('icon').setAttribute('href', faviconHref);
      ensureLink('shortcut icon').setAttribute('href', faviconHref);
      ensureLink('apple-touch-icon').setAttribute('href', faviconHref);
    }

    // JSON-LD structured data (single primary block)
    const ldId = 'ld-primary';
    const existing = document.getElementById(ldId) as HTMLScriptElement | null;
    if (ldJson) {
      const script = existing ?? document.createElement('script');
      script.type = 'application/ld+json';
      script.id = ldId;
      script.text = JSON.stringify(ldJson);
      if (!existing) head.appendChild(script);
    } else if (existing) {
      existing.remove();
    }

    // Viewport (ensure present)
    let viewport = head.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement('meta');
      viewport.setAttribute('name', 'viewport');
      viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
      head.appendChild(viewport);
    }
  }, [title, description, keywords, image, canonicalPath, type, noindex, faviconHref, ldJson]);
};